import React, {ReactElement, useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {TextField} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {Button, Logo} from '@customink/pigment-react';
import CreativeRightsAgreement from '../CreativeRightsAgreement';
import Label from '../Label';
import XCircle from '../../assets/icons/x_circle.svg';
import {
  StoreCreateParamsType,
  useCreateStoreMutation
} from '../../api/storesApi';
import services from '../../api/remoteServices';
import './index.scss';

interface StoreCreationError {
  data?: {
    errors?: string;
  };
}

const NewStore: React.FC = (): ReactElement => {
  const [storeSlug, setStoreSlug] = useState<string>('');
  const [
    triggerUseCreateStoreMutation,
    {isSuccess: isCreateStoreSuccess, error}
  ] = useCreateStoreMutation();
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [slugInputProps, setSlugInputProps] = useState({});
  const [nameInputProps, setNameInputProps] = useState({});
  const [agreementChecked, setAgreementChecked] = useState(false);

  const handleCreateStoreSuccess = () => {
    if (isCreateStoreSuccess) {
      history.push(`/${storeSlug}/manage/dashboard?new=true`);
    }
  };

  const handleCreateStoreFailure = () => {
    if (error && (error as StoreCreationError).data) {
      if (
        (error as StoreCreationError).data.errors.includes(
          'not allowed to create'
        )
      ) {
        setErrorMessage('At this time, only invited users can create a store.');
      } else if (
        (error as StoreCreationError).data.errors.includes(
          'Validation failed: Url slug has already been taken'
        )
      ) {
        setSlugInputProps({
          error: true,
          helperText: 'That URL is already taken. Please try a different URL.'
        });
      } else {
        setErrorMessage('Please try again later.');
      }
    }
  };
  useEffect(handleCreateStoreSuccess, [isCreateStoreSuccess]);
  useEffect(handleCreateStoreFailure, [error]);

  const handleNameOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enteredName = e.target.value;
    setName(enteredName);
    const defaultSlug = enteredName
      .toLowerCase()
      .replace(/\s/g, '-')
      .replace(/[^a-zA-Z0-9-]/g, '');
    setStoreSlug(defaultSlug);
    setNameInputProps({});
    setSlugInputProps({});
  };

  const handleStoreSlugOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enteredSlug = e.target.value;
    setStoreSlug(enteredSlug);
    if (/[^a-zA-Z0-9-]/g.test(enteredSlug)) {
      setSlugInputProps({
        error: true,
        helperText:
          'Your URL cannot have spaces or special characters. Please try again.'
      });
    } else {
      setSlugInputProps({});
    }
  };

  const handleNameOnBlur = () => {
    if (name === '') {
      setNameInputProps({
        error: true,
        helperText: 'Your store must have a name.'
      });
    }
  };

  const handleStoreSlugOnBlur = () => {
    if (storeSlug === '') {
      setSlugInputProps({
        error: true,
        helperText: 'Your store must have a URL.'
      });
    }
  };

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      setErrorMessage('');
      const storeParams: StoreCreateParamsType = {
        name,
        url_slug: storeSlug,
        status: 'draft'
      };
      setStoreSlug(storeSlug);
      triggerUseCreateStoreMutation(storeParams);
    },
    [name, storeSlug]
  );

  return (
    <div className="newStore">
      <a
        className="newStore-sidebar"
        href={services.homePage}
        rel="noopener noreferrer">
        <Logo
          variant="inky"
          height="40px"
          onClick={() => {
            /* nothing */
          }}
          className="app-logo"
          placeholder="inky logo"
        />
      </a>
      <div className="newStore-main">
        <h1>
          Let&rsquo;s do this! First, we need to give your store
          <b className="bolder"> a name and a home.</b>
        </h1>
        <p className="subheading">
          Don&rsquo;t worry, you can always make changes later.
        </p>
        {errorMessage && <p className="error"> {errorMessage} </p>}
        <form>
          <Label required>Store Name</Label>
          <TextField
            className="newStore-form-input"
            id="name"
            value={name}
            variant="outlined"
            required
            autoFocus
            {...nameInputProps}
            onChange={handleNameOnChange}
            onBlur={handleNameOnBlur}
          />
          <Label required>Store URL</Label>
          <TextField
            className="newStore-form-input"
            id="storeSlug"
            value={storeSlug}
            variant="outlined"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  customink.com/s/
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {Object.keys(slugInputProps).length > 1 && (
                    <InputAdornment position="end">
                      <img src={XCircle} alt="Error" />
                    </InputAdornment>
                  )}
                </>
              )
            }}
            {...slugInputProps}
            onChange={handleStoreSlugOnChange}
            onBlur={handleStoreSlugOnBlur}
          />
          <CreativeRightsAgreement
            toggleChecked={() => {
              setAgreementChecked(prevCheck => !prevCheck);
            }}
          />
          <Button
            size="large"
            type="submit"
            disabled={
              name.length === 0 ||
              storeSlug.length === 0 ||
              Object.keys(nameInputProps).length > 1 ||
              Object.keys(slugInputProps).length > 1 ||
              errorMessage.length > 0 ||
              !agreementChecked
            }
            onClick={onSubmit}>
            Create My Store
          </Button>
        </form>
      </div>
    </div>
  );
};

export default NewStore;
